import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getIncentiveCountries, getMyIncentives, saveIncentiveCountry } from "@/lib/api";
import { localeFor } from "@/lib/money";

// Incentive slabs per country (India INR / Sri Lanka LKR / UAE AED), set centrally:
// FE sales, FE conversion and the nutrition consult amount in Global Admin → Incentives;
// Lead nurture slabs on the Lead Nurture dashboard. Gym Settings shows them read-only.

const LABELS = {
  sales: { title: "FE sales (Sales Executive)", hint: "Selling the front-end challenge on New Consult. One rate on the month's total." },
  fe_conversion: { title: "FE retention (Accountability Coach)", hint: "Front-end member moved to 12 Week / 24 Week / Annual. The whole month's net received is paid at the rate of the slab it falls in." },
  leads: { title: "Lead nurture (executive)", hint: "The executive whose lead became a sale." },
  blood_test_cost: { title: "Blood test cost (per member)", hint: "What one member's blood tests cost us (both tests). Taken off a Sales Executive's FE sales total once for every consult sold with blood tests ticked, and used for the blood test reports." },
  nutrition_rate: { title: "Nutrition consult", hint: "Flat amount per completed nutrition consult for a front-end member." },
  sales_bonus: { title: "FE sales conversion bonus", hint: "Extra rate on top of the FE sales slab for a Sales Executive when enough of the month's consults are sold and have started. Sales that haven't started don't count." },
};

function Bonus({ value, setValue, canEdit, code }) {
  const b = value || { enabled: false, min_conversion: 0.5, min_consults: 15, extra_rate: 0.01, min_avg_price: 0 };
  const set = (k, v) => setValue({ ...b, [k]: v });
  const summary = b.enabled
    ? `+${(b.extra_rate * 100).toFixed(1)} percentage points when at least ${Math.round(b.min_conversion * 100)}% of a rep's consults in the month (at least ${b.min_consults}) are sold and have started${b.min_avg_price ? `, with started 6 Week Challenges averaging at least ${code} ${money(code, b.min_avg_price)} before tax` : ""}.`
    : "Off.";
  if (!canEdit) return <div className="text-sm text-zinc-300" data-testid="incentives-bonus-view">{summary}</div>;
  return (
    <div className="space-y-2 text-sm" data-testid="incentives-bonus-edit">
      <label className="flex items-center gap-2 text-zinc-300">
        <input type="checkbox" checked={!!b.enabled} onChange={(e) => set("enabled", e.target.checked)} /> On
      </label>
      <div className="grid grid-cols-4 gap-2 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
        <div>Min conversion %</div><div>Min consults</div><div>Extra (points)</div><div>Min avg 6WC price ({code}, ex tax)</div>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <Input type="number" min={1} max={100} value={Math.round(b.min_conversion * 100)} onChange={(e) => set("min_conversion", Number(e.target.value) / 100)}
          className="bg-[#0a0a0a] border-[#262626] font-mono" />
        <Input type="number" min={1} value={b.min_consults} onChange={(e) => set("min_consults", Number(e.target.value))}
          className="bg-[#0a0a0a] border-[#262626] font-mono" />
        <Input type="number" step="0.5" min={0} max={10} value={+(b.extra_rate * 100).toFixed(2)} onChange={(e) => set("extra_rate", Number(e.target.value) / 100)}
          className="bg-[#0a0a0a] border-[#262626] font-mono" />
        <Input type="number" min={0} value={b.min_avg_price || 0} onChange={(e) => set("min_avg_price", Number(e.target.value))}
          className="bg-[#0a0a0a] border-[#262626] font-mono" />
      </div>
      <div className="text-[11px] text-zinc-500">{summary}</div>
    </div>
  );
}

const money = (code, n) => (n == null ? "∞" : new Intl.NumberFormat(localeFor(code), { maximumFractionDigits: 0 }).format(n));

function Slabs({ rows, setRows, code, canEdit, testid }) {
  const upd = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v === "" ? null : Number(v) } : r)));
  if (!canEdit) {
    return (
      <table className="w-full text-sm" data-testid={testid}>
        <thead><tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-left">
          <th className="py-1 font-medium">From {code}</th><th className="py-1 font-medium">To {code}</th><th className="py-1 font-medium text-right">Rate</th>
        </tr></thead>
        <tbody>
          {(rows || []).map((r, i) => (
            <tr key={i} className="border-t border-[#1a1a1a]">
              <td className="py-1.5 font-mono">{money(code, r.min)}</td><td className="py-1.5 font-mono">{money(code, r.max)}</td>
              <td className="py-1.5 font-mono text-right text-emerald-300">{(r.rate * 100).toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
        <div className="col-span-4">From {code}</div><div className="col-span-4">To {code} (empty = ∞)</div><div className="col-span-3">Rate (0.05 = 5%)</div>
      </div>
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-12 gap-2">
          <Input type="number" value={r.min ?? ""} onChange={(e) => upd(i, "min", e.target.value)} className="col-span-4 bg-[#0a0a0a] border-[#262626] font-mono" />
          <Input type="number" value={r.max ?? ""} placeholder="∞" onChange={(e) => upd(i, "max", e.target.value)} className="col-span-4 bg-[#0a0a0a] border-[#262626] font-mono" />
          <Input type="number" step="0.005" value={r.rate ?? ""} onChange={(e) => upd(i, "rate", e.target.value)} className="col-span-3 bg-[#0a0a0a] border-[#262626] font-mono" />
          <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="col-span-1 text-zinc-500 hover:text-rose-400" aria-label="Remove slab">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" className="border-[#262626]"
        onClick={() => { const last = rows[rows.length - 1]; const from = last?.max ?? 0; setRows([...rows, { min: from, max: null, rate: last?.rate ?? 0.05 }]); }}>
        <Plus className="h-3.5 w-3.5 mr-1" /> Add slab
      </Button>
    </div>
  );
}

function CountryCard({ c, fields, canEdit, onSaved }) {
  const [draft, setDraft] = useState(() => Object.fromEntries(fields.map((f) => [f, c[f]])));
  const [busy, setBusy] = useState(false);
  const fieldKey = fields.join(",");
  // Reset the draft only when the saved values change (not on every parent redraw)
  useEffect(() => { setDraft(Object.fromEntries(fieldKey.split(",").map((f) => [f, c[f]]))); }, [c, fieldKey]);
  const unsaved = fields.filter((f) => !c.saved?.[f]);
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(fields.map((f) => [f, ["nutrition_rate", "blood_test_cost"].includes(f) ? Number(draft[f]) || 0 : draft[f]]));
      onSaved(await saveIncentiveCountry(c.country, body));
      toast.success(`${c.country} incentives saved`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <section className="card-surface p-5 space-y-4" data-testid={`incentives-${c.country}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-bold">{c.country} <span className="text-sm font-normal text-zinc-500">· {c.currency_code}</span></h3>
          <div className="text-xs text-zinc-500">{(c.gyms || []).join(", ")}</div>
          {canEdit && unsaved.length > 0 && c.country !== "India" && (
            <div className="mt-1 text-xs text-amber-300" data-testid={`incentives-needs-${c.country}`}>
              Still today&apos;s per-gym values (rupee numbers). Set them in {c.currency_code} and save.
            </div>
          )}
          {c.updated_by && <div className="mt-1 text-[11px] text-zinc-600">Last saved by {c.updated_by}</div>}
        </div>
        {canEdit ? (
          <Button onClick={save} disabled={busy} className="rounded-full bg-blue-600 hover:bg-blue-500" data-testid={`incentives-save-${c.country}`}>
            <Save className="h-4 w-4 mr-1.5" />{busy ? "Saving…" : `Save ${c.country}`}
          </Button>
        ) : (
          <span className="text-xs text-zinc-500 inline-flex items-center gap-1"><Lock className="h-3 w-3" />Master Admin only</span>
        )}
      </div>
      <div className={`grid gap-5 ${fields.length > 1 ? "lg:grid-cols-2" : ""}`}>
        {fields.map((f) => (
          <div key={f} className="space-y-2">
            <div>
              <div className="eyebrow">{LABELS[f].title}</div>
              <div className="text-[11px] text-zinc-500">{LABELS[f].hint}</div>
            </div>
            {f === "sales_bonus" ? (
              <Bonus value={draft[f]} setValue={(v) => setDraft((d) => ({ ...d, [f]: v }))} canEdit={canEdit} code={c.currency_code} />
            ) : f === "blood_test_cost" ? (
              canEdit ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-zinc-400">{c.currency_code}</span>
                  <Input type="number" min={0} value={draft[f] ?? 0} onChange={(e) => setDraft((d) => ({ ...d, [f]: e.target.value }))}
                    className="w-32 bg-[#0a0a0a] border-[#262626] font-mono" data-testid={`incentives-blood-${c.country}`} />
                  <span className="text-xs text-zinc-500">per member with blood tests (both tests){Number(draft[f]) === 0 ? " · 0 = nothing taken off" : ""}</span>
                </div>
              ) : <div className="font-mono">{c.currency_code} {money(c.currency_code, c[f])} per member with blood tests (both tests)</div>
            ) : f === "nutrition_rate" ? (
              canEdit ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-zinc-400">{c.currency_code}</span>
                  <Input type="number" min={0} value={draft[f] ?? 0} onChange={(e) => setDraft((d) => ({ ...d, [f]: e.target.value }))}
                    className="w-32 bg-[#0a0a0a] border-[#262626] font-mono" data-testid={`incentives-nutrition-${c.country}`} />
                  <span className="text-xs text-zinc-500">per consult{Number(draft[f]) === 0 ? " · 0 = no payout" : ""}</span>
                </div>
              ) : <div className="font-mono">{c.currency_code} {money(c.currency_code, c[f])} per consult{!c[f] ? " (not set: nothing is paid)" : ""}</div>
            ) : (
              <Slabs rows={draft[f] || []} setRows={(rows) => setDraft((d) => ({ ...d, [f]: rows }))} code={c.currency_code}
                canEdit={canEdit} testid={`incentives-${f}-${c.country}`} />
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// fields: which incentives to show. mine: only the active gym's country (read-only Settings view).
export default function IncentiveCountries({ fields, canEdit, mine = false }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (mine ? getMyIncentives().then((c) => [c].filter(Boolean)) : getIncentiveCountries().then((d) => d.countries))
      .then(setRows).catch(() => setRows([]));
  }, [mine]);
  if (!rows) return <div className="text-sm text-zinc-500">Loading…</div>;
  return (
    <div className="space-y-4">
      {rows.map((c) => (
        <CountryCard key={c.country} c={c} fields={fields} canEdit={canEdit && !mine}
          onSaved={(saved) => setRows((rs) => rs.map((r) => (r.country === saved.country ? saved : r)))} />
      ))}
    </div>
  );
}
